//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { useQuery } from '@tanstack/react-query';

import { getContentLibraryAggregateV2 } from '@apis/content-library/contentLibraryApi';
import { ContentLibraryFilter } from '@apis/content-library/contentLibraryApiModel';
import { ISearchLabelsResponse } from '@apis/gatewayMediaApiModel';
import ClientId from '@models/ClientId';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { FIVE_MINUTES_STALE_TIME } from '@queries/QueryOptionsConstants';

interface UseModeratorLabelsQueryParams {
  enabled?: boolean;
  query: ContentLibraryFilter;
}

/**
 * Moderator labels for the "View All" modal. Unlike the client-side
 * useAllLabelsQuery, this calls the aggregate API with type "UserTag" so only
 * moderator-applied labels (and their counts) are returned, scoped to the same
 * moderation query used by the inline label filter.
 */
export const useModeratorLabelsQuery = ({
  enabled,
  query,
}: UseModeratorLabelsQueryParams) =>
  useQuery({
    queryKey: [
      QueryKeyConstants.MediaFilters,
      QueryKeyConstants.MediaFiltersLabels,
      QueryKeyConstants.MediaFiltersLabelsManage,
      'moderator',
      query,
    ],
    queryFn: ({ signal }) =>
      getContentLibraryAggregateV2({ type: 'UserTag', query }, { signal }),
    select: ({ terms }): ISearchLabelsResponse => ({
      data: {
        count: terms.length,
        indexObjects: terms.map(({ count, termName }) => ({
          clientId: ClientId(0),
          isUpdating: false,
          itemsWithTag: count,
          lastUpdated: 0,
          lastUsed: 0,
          userTag: termName,
        })),
      },
    }),
    enabled,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
